<moodle-credentials-form>
  <div class="flex flex-col gap-4">
    <div>
      <h3 id="moodle-form-heading" class="text-lg font-bold">
        {props.isEditMode ? locale?.t('admin_moodle_title_update') : locale?.t('admin_moodle_title_config')}
      </h3>
      <p class="text-sm text-gray-500 font-semibold mt-1">
        {props.isEditMode ? locale?.t('admin_moodle_subtitle_update') : locale?.t('admin_moodle_subtitle_config')}
      </p>
    </div>

    <!-- What has to be true on the Moodle side before any of this works. Leads for the
         same reason OnPoint's super-admin requirement does: it is the most common reason
         a first attempt fails. -->
    <div class="alert alert-info text-sm">
      <i class="icon-info" aria-hidden="true"></i>
      <span>
        {locale?.t('admin_moodle_prereq')}
        <a href="{state.setupDocs}" class="link link-primary ms-1">
          {locale?.t('admin_moodle_prereq_help')}
        </a>
      </span>
    </div>

    <div class="form-control">
      <label class="label" for="moodle-site-url">
        <span class="label-text font-semibold">
          {locale?.t('admin_moodle_siteurl')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <input
        id="moodle-site-url"
        type="url"
        class="input input-bordered w-full"
        placeholder="https://learn.customer.edu"
        value="{state.siteBaseUrl}"
        oninput="{(e) => onField('siteBaseUrl', e)}"
        required
      />
      <p class="text-xs text-gray-500 mt-1">{locale?.t('admin_moodle_siteurl_help')}</p>
    </div>

    <div class="form-control">
      <label class="label" for="moodle-token">
        <span class="label-text font-semibold">
          {locale?.t('admin_moodle_token')} <span class="text-error" aria-hidden="true">*</span>
        </span>
      </label>
      <!-- In edit mode the box carries a masked PLACEHOLDER, not a value: a stored token
           cannot be read back, and putting bullets in `value` would send them to the
           server as the credential the moment someone saved without retyping. Empty means
           "keep what is stored"; typing replaces it. -->
      <input
        id="moodle-token"
        type="password"
        class="input input-bordered w-full"
        autocomplete="new-password"
        placeholder="{props.isEditMode ? state.secretMask : ''}"
        value="{state.token}"
        oninput="{(e) => onField('token', e)}"
        required="{!props.isEditMode}"
      />
      <p class="text-xs text-gray-500 mt-1">{locale?.t('admin_moodle_token_help')}</p>
      <p if="{props.isEditMode && !state.token}" class="text-xs text-gray-600 mt-1 flex items-center gap-1">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_lms_secret_stored_help')}
      </p>
      <p if="{props.isEditMode && state.token}" class="text-xs text-gray-500 mt-1">
        {locale?.t('admin_moodle_token_replace_help')}
      </p>
    </div>

    <!-- How the credential is handled, answering the question the token box just raised. -->
    <div class="rounded border border-base-300 bg-base-200 p-3">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="icon-metalark-lock" aria-hidden="true"></i>
        {locale?.t('admin_onpoint_credentials_encrypted')}
      </p>
      <p class="text-xs text-gray-600 mt-1">{locale?.t('admin_moodle_credentials_notice')}</p>
    </div>

    <!-- The operating mode, populated by a successful test. This is the reason the test
         step exists: a Core-only connection creates courses a learner cannot open, and an
         administrator has to see that before pushing rather than after. -->
    <div if="{state.probe}" class="rounded border p-3 {state.probe.fullContentPush ? 'border-success bg-success/10' : 'border-warning bg-warning/10'}">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="{state.probe.fullContentPush ? 'icon-check_circle' : 'icon-warning'}" aria-hidden="true"></i>
        {state.probe.fullContentPush
          ? locale?.t('admin_moodle_mode_full')
          : locale?.t('admin_moodle_mode_coreonly')}
      </p>
      <p class="text-xs text-gray-700 mt-1">
        {state.probe.fullContentPush
          ? locale?.t('admin_moodle_mode_full_detail')
          : locale?.t('admin_moodle_mode_coreonly_detail')}
      </p>

      <!-- Named rather than summarized: "the plugin is missing" sends an admin to install
           something that may already be installed, when the real problem is a function
           left off the external service the token is scoped to. -->
      <p if="{!state.probe.fullContentPush && state.probe.missingFunctions?.length}"
         class="text-xs text-gray-600 mt-2 break-all">
        {locale?.t('admin_moodle_plugin_missing_functions', { functions: state.probe.missingFunctions.join(', ') })}
      </p>

      <!-- The remedy, in one line, for the case the install link alone does not serve.
           The panel further down carries real fix steps, but it is built from the CORE
           severities, so a token whose core list is complete and whose service simply
           omits the plugin's functions gets the diagnosis with no cure — which is how an
           administrator with the plugin already installed ends up reinstalling it. Kept to
           a sentence rather than repeating the steps: the install guide's "After
           Installing" section has them, and the link sits directly below this. -->
      <p if="{!state.probe.fullContentPush && state.probe.missingFunctions?.length}"
         class="text-xs text-gray-700 mt-1">
        {locale?.t('admin_moodle_plugin_authorize_hint')}
      </p>
      <!-- Restored under METALARK-1164, now that there is a real page to point at. It sits
           BELOW the missing-function line on purpose: that line is the actionable half in
           the commoner case, where the plugin is installed and a function was left off the
           external service, and an install link offered first sends someone to reinstall
           software they already have. The guide's own "After Installing" section covers
           authorizing the functions, so the link is right for both cases. -->
      <p if="{!state.probe.fullContentPush}" class="text-xs mt-2">
        <a href="{state.pluginDocs}" class="link link-primary">
          {locale?.t('admin_moodle_plugin_install_help')}
        </a>
      </p>
    </div>

    <!-- What the TOKEN can do, which is a different question from whether the plugin is
         there and has a different remedy. A Moodle token is scoped to an External Service
         with a hand-picked function list; one built without core_course_update_courses
         authenticates perfectly and then creates courses that never get tagged. Nothing
         checked this before, so that was found after a migration rather than at setup. -->
    <div if="{tokenGaps().length > 0}"
         class="rounded border p-3 {state.probe.canPush === false ? 'border-error bg-error/10' : 'border-warning bg-warning/10'}"
         role="alert">
      <p class="text-sm font-semibold flex items-center gap-2">
        <i class="{state.probe.canPush === false ? 'icon-error_outline' : 'icon-warning'}" aria-hidden="true"></i>
        {state.probe.canPush === false
          ? locale?.t('admin_moodle_token_incomplete')
          : locale?.t('admin_moodle_token_limited')}
      </p>

      <!-- Consequence first, function name second. "core_course_update_courses is missing"
           is not actionable to most administrators; "courses cannot be tagged" is, and the
           function name is what they then paste into the service definition. -->
      <ul class="mt-2 flex flex-col gap-1.5 list-disc ms-4">
        <li each="{gap in tokenGaps()}" class="text-xs text-gray-700">
          <span class="font-medium">{gap.consequence}</span>
          <span class="block font-mono break-all text-gray-600">{gap.fn}</span>
        </li>
      </ul>

      <p class="text-xs text-gray-700 mt-2 font-medium">{locale?.t('admin_moodle_token_fix_title')}</p>
      <ol class="mt-1 flex flex-col gap-1 list-decimal ms-4">
        <li each="{step in tokenFixSteps()}" class="text-xs text-gray-700">{step}</li>
      </ol>
    </div>

    <!-- The upload ceiling, which is per-site and is the most common cause of a failed
         push. Unknown is stated as unknown rather than shown as "no limit". -->
    <p if="{state.probe}" class="text-xs text-gray-600">
      <span if="{state.probe.maxUploadBytes}">
        {locale?.t('admin_moodle_maxupload', { size: formatFileSize(state.probe.maxUploadBytes) })}
      </span>
      <span if="{!state.probe.maxUploadBytes}">{locale?.t('admin_moodle_maxupload_unknown')}</span>
    </p>

    <!-- Reachable categories. Read-only here: which one a push goes to is chosen when the
         migration target is created, exactly as OnPoint's customer is. -->
    <div if="{state.probe?.categories?.length > 0}" class="rounded border border-base-300 bg-base-200 p-3">
      <p class="text-sm font-semibold">
        {locale?.t('admin_moodle_categories_found', { count: state.probe.categories.length })}
      </p>
      <ul class="mt-2 flex flex-col gap-1">
        <li each="{c in state.probe.categories}" class="text-sm text-gray-700">
          {c.name} <span class="text-gray-500">({c.categoryId})</span>
        </li>
      </ul>
      <p class="text-xs text-gray-500 mt-2">{locale?.t('admin_moodle_categories_note')}</p>
    </div>

    <div if="{state.successMessage}" class="alert alert-success text-sm" role="status">
      <i class="icon-check" aria-hidden="true"></i>
      <span>{state.successMessage}</span>
    </div>

    <div if="{state.errorMessage}" class="alert alert-error text-sm" role="alert">
      <i class="icon-error" aria-hidden="true"></i>
      <span>{state.errorMessage}</span>
    </div>
  </div>

  <div class="modal-action">
    <button
      type="button"
      class="btn btn-ghost"
      onclick="{handleCancel}"
      disabled="{state.busy}"
      aria-label="{locale?.t('cancel')}"
    >
      {locale?.t('cancel')}
    </button>
    <button
      type="button"
      class="btn btn-outline"
      onclick="{handleTest}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_testconnection')}"
    >
      <span if="{state.busy === 'test'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'test' ? locale?.t('testing') : locale?.t('admin_onpoint_testconnection')}
    </button>
    <button
      type="button"
      class="btn btn-primary"
      onclick="{handleSave}"
      disabled="{!isValid() || state.busy}"
      aria-label="{locale?.t('admin_onpoint_save')}"
    >
      <span if="{state.busy === 'save'}" class="loading loading-spinner loading-sm"></span>
      {state.busy === 'save' ? locale?.t('saving') : locale?.t('admin_onpoint_save')}
    </button>
  </div>

  <script>
    import { getRepositoryService } from '../../../services/service-loader';
    import { isCredentialSafeUrl } from '../../../utils/localMachineHost';

    /**
     * Where a Moodle administrator learns to set the site up: enable web services, define
     * the External Service with the functions MetaLark calls, and issue a token.
     *
     * A real page, unlike what stood here before. The previous value pointed at
     * `#support?topic=moodle-connector-plugin` — the support page has never read a `topic`
     * parameter, so it silently landed on the index, which carries no Moodle article at
     * all.
     */
    const SETUP_DOCS = '#support-moodle-setup';

    /**
     * Where a Moodle administrator learns to install the companion plugin: both install
     * routes, authorizing its four functions afterwards, and a hosting checklist — some
     * hosting permits no third-party plugin at all, MoodleCloud included.
     *
     * A different page from SETUP_DOCS, and deliberately so: that one is done once in a
     * browser to get a token, this one may need filesystem access and a change request, and
     * a site can be perfectly well set up without ever installing the plugin.
     *
     * Shown only where full content push is unavailable, which is the only moment it helps.
     * Added under METALARK-1164; the link that stood here before was removed rather than
     * re-pointed, because at the time the page genuinely did not exist.
     */
    const PLUGIN_DOCS = '#support-moodle-plugin-install';

    /**
     * What an already-stored secret looks like in the box. A placeholder only — see the
     * token input. Eight bullets rather than the real length, which is not ours to leak.
     */
    const SECRET_MASK = '••••••••';

    export default {
      state: {
        siteBaseUrl: '',
        token: '',
        // On state, not read from the module const directly: Riot resolves template
        // expressions against the instance, so a module-scoped value renders as empty.
        setupDocs: SETUP_DOCS,
        pluginDocs: PLUGIN_DOCS,
        secretMask: SECRET_MASK,
        /**
         * The last successful probe: mode, missing functions, upload ceiling, categories.
         * Null until a test or save succeeds, and cleared on any edit so a stale "full
         * content push" panel can never describe values that were since changed.
         */
        probe: null,
        successMessage: '',
        errorMessage: '',
        // 'test' | 'save' | null — which action is in flight, so each button shows its own
        // spinner rather than both.
        busy: null
      },

      onMounted() {
        // Re-credentialing an existing connection prefills the site it was created with;
        // the token is never returned by the API, so it is always retyped.
        this.update({
          siteBaseUrl: this.props.siteBaseUrl || '',
          token: '',
          probe: null,
          successMessage: '',
          errorMessage: '',
          busy: null
        });
      },

      isValid() {
        const { siteBaseUrl, token } = this.state;
        if (!siteBaseUrl?.trim()) return false;
        // A blank token is valid ONLY when editing, where it means "keep the stored one".
        // On a create there is nothing to keep.
        if (!token?.trim() && !this.props.isEditMode) return false;
        // The token rides on every request as a parameter, so plaintext is refused up front
        // rather than at save time. Loopback and Docker's host alias are exempt, matching what
        // `MoodleProvider.validateLocation` accepts: the local stack serves Moodle on
        // `http://host.docker.internal:8080`, which this form used to refuse.
        return isCredentialSafeUrl(siteBaseUrl);
      },

      onField(key, e) {
        // Editing invalidates any prior probe, so a stale mode panel cannot imply the
        // current values were the ones verified.
        this.update({
          [key]: e.target.value,
          successMessage: '',
          errorMessage: '',
          probe: null
        });
      },

      payload() {
        const body = { siteBaseUrl: this.state.siteBaseUrl.trim() };
        // Omitted rather than sent empty: the server reads an absent token on an update as
        // "keep the stored one", and an empty string would be a credential of length zero.
        const token = this.state.token.trim();
        if (token) body.token = token;
        return body;
      },

      /**
       * Bytes as a readable size. Local rather than a `tools` method because there is no
       * centralized byte formatter yet and two other components already carry their own
       * (`key-file-upload-modal`, `sharepoint-site-selector`). The numeric part goes
       * through `tools.formatNumber` so the decimal separator follows the user's locale.
       */
      /**
       * The token's gaps, worst first, each paired with what it actually costs.
       *
       * Keyed by capability rather than by function name because the function name is not
       * the useful half. An administrator reading "core_course_update_courses is missing"
       * has to go and find out what that does; "courses cannot be tagged" is the thing they
       * can decide about, and the function name below it is what they paste into Moodle.
       *
       * A capability with no key of its own falls back to naming the function, so a
       * function added to MOODLE_CORE_FUNCTIONS server-side degrades to something truthful
       * here instead of rendering a blank bullet.
       */
      tokenGaps() {
        const probe = this.state.probe;
        if (!probe) return [];
        const describe = (fn, severity) => ({
          fn,
          severity,
          consequence: this.locale?.t(`admin_moodle_token_gap_${fn}`)
            || this.locale?.t('admin_moodle_token_gap_generic', { fn })
            || fn,
        });
        return [
          ...(probe.missingRequired || []).map(fn => describe(fn, 'required')),
          ...(probe.missingDegraded || []).map(fn => describe(fn, 'degraded')),
          ...(probe.missingOptional || []).map(fn => describe(fn, 'optional')),
        ];
      },

      /**
       * How to fix it, in the order the screens appear in Moodle.
       *
       * Written as steps rather than a link because this is a five-click path through Site
       * administration that an administrator will not guess, and the functions they need to
       * add are already listed directly above.
       */
      tokenFixSteps() {
        return [
          this.locale?.t('admin_moodle_token_fix_step1'),
          this.locale?.t('admin_moodle_token_fix_step2'),
          this.locale?.t('admin_moodle_token_fix_step3'),
          this.locale?.t('admin_moodle_token_fix_step4'),
        ].filter(Boolean);
      },

      formatFileSize(bytes) {
        if (!bytes || bytes <= 0) return '';
        const k = 1024;
        const units = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), units.length - 1);
        const value = Math.round((bytes / Math.pow(k, i)) * 100) / 100;
        return `${this.tools?.formatNumber(value, 2) ?? value} ${units[i]}`;
      },

      async handleTest() {
        if (!this.isValid()) return;
        this.update({ busy: 'test', successMessage: '', errorMessage: '', probe: null });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          // With the box left blank there is no credential to test but the stored one, and
          // the categories endpoint is precisely "probe this connection with what is
          // stored" — same response shape, so nothing downstream changes.
          const result = body.token
            ? await service.testMoodleConnection(body)
            : await service.getMoodleCategories(this.props.repositoryId);
          this.update({
            busy: null,
            probe: result,
            successMessage: this.locale?.t('admin_moodle_test_success') || 'Connected'
          });
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      async handleSave() {
        if (!this.isValid()) return;
        this.update({ busy: 'save', successMessage: '', errorMessage: '' });
        try {
          const service = await getRepositoryService();
          const body = this.payload();
          if (this.props.repositoryId) body.repositoryId = this.props.repositoryId;
          const result = await service.connectMoodle(body);
          this.update({ busy: null, probe: result });
          if (this.props.onsaved) this.props.onsaved(result?.repository);
        } catch (error) {
          this.update({ busy: null, errorMessage: this.messageFor(error) });
        }
      },

      /**
       * The API's own message is the useful one here — it distinguishes a rejected token
       * from an unreachable site from a token whose user cannot read categories, and a
       * generic fallback would discard that distinction.
       */
      messageFor(error) {
        return error?.response?.data?.error?.message
          || error?.response?.data?.message
          || error?.message
          || this.locale?.t('admin_moodle_test_failed')
          || 'Connection failed';
      },

      handleCancel() {
        if (this.props.oncancel) this.props.oncancel();
      }
    }
  </script>
</moodle-credentials-form>
